import imageCompression from 'browser-image-compression';

// Phase 5 5.40 — client-side receipt image compression.
//
// Runs in a web worker (via browser-image-compression) before a receipt photo
// is uploaded, so large phone-camera photos don't hit the backend's 2MB size
// guard (see day-receipt-form.jsx handleFileChange) and don't burn mobile data.
//
// PDFs and any other non-image MIME type are returned UNCHANGED — a <canvas>
// cannot re-render a PDF, so there is nothing to compress.
const MAX_SIZE_MB = 1;
const MAX_WIDTH_OR_HEIGHT = 2000;
const QUALITY = 0.85;

/**
 * Compress an image File before upload.
 *
 * @param {File} file - the picked file (image, PDF, or other)
 * @returns {Promise<File>} the compressed file, or the original file if it is
 *   not an image, or if compression fails for any reason (never blocks the
 *   upload — a failed compression falls back to the original file).
 */
export async function compressReceiptImage(file) {
    if (!file || !(file instanceof File)) return file;

    // Not an image (e.g. application/pdf) — nothing to compress.
    if (!file.type || !file.type.startsWith('image/')) {
        return file;
    }

    try {
        const compressed = await imageCompression(file, {
            maxSizeMB: MAX_SIZE_MB,
            maxWidthOrHeight: MAX_WIDTH_OR_HEIGHT,
            initialQuality: QUALITY,
            useWebWorker: true,
            // HEIC/HEIF from iPhones: browser-image-compression converts these
            // via its optional heic2any integration; if that path errors out
            // (unsupported browser, etc.) the catch below falls back to the
            // original file so the user is never blocked from uploading.
            fileType: undefined, // preserve original mime type where possible
        });

        // Preserve the original filename — browser-image-compression keeps it,
        // but guard in case a future version changes that behavior.
        if (compressed.name !== file.name) {
            return new File([compressed], file.name, { type: compressed.type });
        }
        return compressed;
    } catch (err) {
        // Never block the upload on a compression failure — fall back to the
        // original file. The caller's existing size guard is the backstop.
        return file;
    }
}

export default compressReceiptImage;
